import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Bell,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Link2,
  ListFilter,
  Loader2,
  MapPin,
  Phone,
  Plus,
  Send,
  Users,
  Video
} from 'lucide-react';
import { apiRequest } from '../../services/api';
import { TENANT } from '../../config';
import { Button } from '../../components/new-components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/new-components/ui/dialog';

type CalendarGuest = {
  id?: string;
  contact_id?: string;
  user_id?: string;
  event_lead_id?: string;
  guest_type: string;
  display_name?: string;
  email?: string;
  phone?: string;
  is_required: boolean;
  attendance_status?: string;
  notification_status?: string;
  internal_notes?: string;
};

type CalendarMeetingType = {
  id: string;
  key: string;
  label: string;
  purpose?: string;
  default_cta?: string;
  duration_minutes: number;
  location_type: string;
  host_name?: string;
  host_email?: string;
  internal_only: boolean;
};

type CalendarEventRecord = {
  id: string;
  title: string;
  meeting_type_key?: string;
  meeting_type_label?: string;
  description?: string;
  internal_notes?: string;
  meeting_objective?: string;
  priority?: string;
  status: string;
  follow_up_state?: string;
  starts_at: string;
  ends_at: string;
  timezone: string;
  host_name?: string;
  host_email?: string;
  location_type: string;
  virtual_meeting_url?: string;
  phone_direction?: string;
  phone_number?: string;
  backup_phone_number?: string;
  dial_in_notes?: string;
  venue_name?: string;
  address?: string;
  room_suite?: string;
  arrival_instructions?: string;
  campaign_name?: string;
  event_campaign_name?: string;
  lead_first_name?: string;
  lead_last_name?: string;
  lead_email?: string;
  source?: string;
  medium?: string;
  owner?: string;
  segment?: string;
  deal_room_access_status?: string;
  guests: CalendarGuest[];
  post_meeting_summary?: Record<string, unknown>;
};

type CalendarView = 'day' | 'week' | 'month' | 'agenda';

type CreateForm = {
  title: string;
  meeting_type_key: string;
  starts_at: string;
  ends_at: string;
  timezone: string;
  host_name: string;
  host_email: string;
  location_type: string;
  virtual_meeting_url: string;
  phone_direction: string;
  phone_number: string;
  venue_name: string;
  address: string;
  description: string;
  internal_notes: string;
  meeting_objective: string;
  source: string;
  medium: string;
  owner: string;
  segment: string;
};

const statusOptions = [
  'draft',
  'scheduled',
  'confirmation_sent',
  'updated',
  'rescheduled',
  'canceled',
  'completed',
  'no_show',
  'follow_up_needed',
  'follow_up_sent'
];

const locationOptions = [
  { value: 'virtual_meeting', label: 'Virtual Meeting', icon: Video },
  { value: 'phone_call', label: 'Phone Call', icon: Phone },
  { value: 'in_person', label: 'In-Person Meeting', icon: MapPin },
  { value: 'to_be_determined', label: 'To Be Determined', icon: AlertCircle },
  { value: 'internal_only', label: 'Internal Only', icon: ClipboardCheck }
];

// Who attends a meeting depends on what the company sells. A capital-markets
// tenant meets investors and capital partners; eMerge meets facility operators
// and clinical staff. The first entry is the default for a new guest row.
const guestTypes = TENANT.features.investing
  ? ['investor', 'client', 'prospect', 'operator', 'capital_partner', 'internal_team_member', 'external_advisor', 'other']
  : ['client', 'prospect', 'operator', 'internal_team_member', 'external_advisor', 'other'];
const DEFAULT_GUEST_TYPE = guestTypes[0];

function addMinutes(date: Date, minutes: number) {
  return new Date(date.getTime() + minutes * 60 * 1000);
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function startOfDay(date: Date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function toLocalInput(date: Date) {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
}

function dayKey(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDate(value: string | Date) {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(value));
}

function formatTime(value: string | Date) {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(value));
}

function readableStatus(status: string) {
  return status.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
}

function statusClass(status: string) {
  switch (status) {
    case 'confirmation_sent':
    case 'scheduled':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'updated':
    case 'rescheduled':
    case 'follow_up_needed':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'completed':
    case 'follow_up_sent':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'canceled':
    case 'no_show':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
}

function buildRange(view: CalendarView, anchor: Date) {
  if (view === 'day') {
    const start = startOfDay(anchor);
    return { start, end: addDays(start, 1), days: [start] };
  }

  if (view === 'month') {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = addDays(first, -first.getDay());
    const days = Array.from({ length: 42 }, (_unused, index) => addDays(start, index));
    return { start, end: addDays(start, 42), days };
  }

  if (view === 'agenda') {
    const start = startOfDay(anchor);
    const days = Array.from({ length: 21 }, (_unused, index) => addDays(start, index));
    return { start, end: addDays(start, 21), days };
  }

  const start = addDays(startOfDay(anchor), -anchor.getDay());
  const days = Array.from({ length: 7 }, (_unused, index) => addDays(start, index));
  return { start, end: addDays(start, 7), days };
}

function defaultCreateForm(meetingType?: CalendarMeetingType): CreateForm {
  const nextHour = new Date();
  nextHour.setMinutes(0, 0, 0);
  nextHour.setHours(nextHour.getHours() + 1);
  const duration = meetingType?.duration_minutes || 15;
  return {
    title: meetingType?.label || (TENANT.features.investing ? 'Investor Onboarding Call' : 'Onboarding Call'),
    meeting_type_key: meetingType?.key || 'investor_onboarding',
    starts_at: toLocalInput(nextHour),
    ends_at: toLocalInput(addMinutes(nextHour, duration)),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    host_name: meetingType?.host_name || (TENANT.features.investing ? 'Investor Relations' : TENANT.branding.name),
    host_email: meetingType?.host_email || '',
    location_type: meetingType?.location_type || 'virtual_meeting',
    virtual_meeting_url: '',
    phone_direction: 'admin_calls_guest',
    phone_number: '',
    venue_name: '',
    address: '',
    description: '',
    internal_notes: '',
    meeting_objective: '',
    source: '',
    medium: '',
    owner: '',
    segment: ''
  };
}

const AdminCalendar: React.FC = () => {
  const [events, setEvents] = useState<CalendarEventRecord[]>([]);
  const [meetingTypes, setMeetingTypes] = useState<CalendarMeetingType[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [view, setView] = useState<CalendarView>('week');
  const [anchorDate, setAnchorDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState('all');
  const [meetingTypeFilter, setMeetingTypeFilter] = useState('all');
  const [hostFilter, setHostFilter] = useState('all');
  const [leadStatusFilter, setLeadStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(defaultCreateForm());
  const [guestDrafts, setGuestDrafts] = useState<CalendarGuest[]>([{ guest_type: DEFAULT_GUEST_TYPE, display_name: '', email: '', phone: '', is_required: true }]);
  const [virtualLinkDraft, setVirtualLinkDraft] = useState('');
  const [newGuest, setNewGuest] = useState<CalendarGuest>({ guest_type: DEFAULT_GUEST_TYPE, display_name: '', email: '', phone: '', is_required: true });
  const [followUp, setFollowUp] = useState({
    meeting_outcome: 'Qualified',
    quality: '',
    interest_level: '',
    estimated_capital_range: '',
    objections_questions: '',
    recommended_next_action: '',
    deal_room_access_recommendation: '',
    follow_up_owner: '',
    follow_up_due_date: ''
  });

  const range = useMemo(() => buildRange(view, anchorDate), [view, anchorDate]);
  const selectedEvent = useMemo(() => events.find(calendarEvent => calendarEvent.id === selectedId) || events[0], [events, selectedId]);

  const hosts = useMemo(() => {
    const values = events.map(calendarEvent => calendarEvent.host_email || calendarEvent.host_name).filter(Boolean) as string[];
    return Array.from(new Set(values)).sort();
  }, [events]);

  const eventsByDay = useMemo(() => {
    const grouped: Record<string, CalendarEventRecord[]> = {};
    for (const calendarEvent of events) {
      const key = dayKey(new Date(calendarEvent.starts_at));
      grouped[key] = grouped[key] || [];
      grouped[key].push(calendarEvent);
    }
    for (const key of Object.keys(grouped)) {
      grouped[key].sort((firstEvent, secondEvent) => new Date(firstEvent.starts_at).getTime() - new Date(secondEvent.starts_at).getTime());
    }
    return grouped;
  }, [events]);

  const loadMeetingTypes = useCallback(async () => {
    const response = await apiRequest<CalendarMeetingType[]>('GET', '/admin/calendar/meeting-types');
    if (response.success && response.data) {
      setMeetingTypes(response.data);
      const firstType = response.data.find(meetingType => meetingType.key === 'investor_onboarding') || response.data[0];
      setCreateForm(defaultCreateForm(firstType));
    }
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({
      start: range.start.toISOString(),
      end: range.end.toISOString(),
      status: statusFilter,
      meeting_type: meetingTypeFilter,
      host: hostFilter,
      lead_status: leadStatusFilter
    });
    const response = await apiRequest<CalendarEventRecord[]>('GET', `/admin/calendar?${params.toString()}`);
    if (response.success && response.data) {
      setEvents(response.data);
      if (!selectedId && response.data[0]) setSelectedId(response.data[0].id);
    }
    setLoading(false);
  }, [hostFilter, leadStatusFilter, meetingTypeFilter, range.end, range.start, selectedId, statusFilter]);

  useEffect(() => { loadMeetingTypes(); }, [loadMeetingTypes]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { setVirtualLinkDraft(selectedEvent?.virtual_meeting_url || ''); }, [selectedEvent?.id, selectedEvent?.virtual_meeting_url]);

  const updateCreateForm = (field: keyof CreateForm, value: string) => {
    setCreateForm(previous => ({ ...previous, [field]: value }));
  };

  const handleMeetingTypeChange = (value: string) => {
    const meetingType = meetingTypes.find(item => item.key === value);
    const startsAt = new Date(createForm.starts_at);
    setCreateForm(previous => ({
      ...previous,
      meeting_type_key: value,
      title: meetingType?.label || previous.title,
      ends_at: toLocalInput(addMinutes(startsAt, meetingType?.duration_minutes || 30)),
      host_name: meetingType?.host_name || previous.host_name,
      host_email: meetingType?.host_email || previous.host_email,
      location_type: meetingType?.location_type || previous.location_type
    }));
  };

  const createEvent = async () => {
    setSaving(true);
    const payload = {
      ...createForm,
      starts_at: new Date(createForm.starts_at).toISOString(),
      ends_at: new Date(createForm.ends_at).toISOString(),
      guests: guestDrafts.filter(guest => guest.email || guest.display_name)
    };
    const response = await apiRequest<CalendarEventRecord>('POST', '/admin/calendar', payload);
    if (response.success && response.data) {
      setShowCreate(false);
      setSelectedId(response.data.id);
      setGuestDrafts([{ guest_type: DEFAULT_GUEST_TYPE, display_name: '', email: '', phone: '', is_required: true }]);
      setCreateForm(defaultCreateForm(meetingTypes[0]));
      await loadEvents();
      setMessage('Calendar event created and notifications queued');
    } else {
      setMessage(response.message || response.error || 'Calendar event could not be created');
    }
    window.setTimeout(() => setMessage(''), 3000);
    setSaving(false);
  };

  const updateStatus = async (calendarEvent: CalendarEventRecord, status: string) => {
    const response = await apiRequest<CalendarEventRecord>('PATCH', `/admin/calendar/${calendarEvent.id}/status`, { status });
    if (response.success) {
      await loadEvents();
      setMessage('Meeting status updated');
      window.setTimeout(() => setMessage(''), 2500);
    }
  };

  const sendVirtualLinkUpdate = async () => {
    if (!selectedEvent) return;
    const response = await apiRequest<CalendarEventRecord>('PATCH', `/admin/calendar/${selectedEvent.id}`, {
      virtual_meeting_url: virtualLinkDraft,
      notify_guests: true
    });
    if (response.success) {
      await loadEvents();
      setMessage('Meeting details sent');
      window.setTimeout(() => setMessage(''), 2500);
    }
  };

  const addGuest = async () => {
    if (!selectedEvent || (!newGuest.email && !newGuest.display_name)) return;
    const response = await apiRequest<CalendarEventRecord>('POST', `/admin/calendar/${selectedEvent.id}/guests`, { guests: [newGuest], notify_all: true });
    if (response.success) {
      setNewGuest({ guest_type: DEFAULT_GUEST_TYPE, display_name: '', email: '', phone: '', is_required: true });
      await loadEvents();
      setMessage('Guest added and update sent');
      window.setTimeout(() => setMessage(''), 2500);
    }
  };

  const saveFollowUp = async () => {
    if (!selectedEvent) return;
    const response = await apiRequest<CalendarEventRecord>('POST', `/admin/calendar/${selectedEvent.id}/follow-up`, followUp);
    if (response.success) {
      await loadEvents();
      setMessage('Follow-up saved');
      window.setTimeout(() => setMessage(''), 2500);
    }
  };

  const moveRange = (direction: number) => {
    const amount = view === 'month' ? 31 : view === 'day' ? 1 : 7;
    setAnchorDate(previous => addDays(previous, direction * amount));
  };

  const openCreate = () => {
    setCreateForm(defaultCreateForm(meetingTypes.find(meetingType => meetingType.key === 'investor_onboarding') || meetingTypes[0]));
    setGuestDrafts([{ guest_type: DEFAULT_GUEST_TYPE, display_name: '', email: '', phone: '', is_required: true }]);
    setShowCreate(true);
  };

  const EventCard = ({ calendarEvent }: { calendarEvent: CalendarEventRecord }) => {
    const LocationIcon = locationOptions.find(option => option.value === calendarEvent.location_type)?.icon || CalendarDays;
    return (
      <button
        type="button"
        onClick={() => setSelectedId(calendarEvent.id)}
        className={`w-full rounded-md border bg-white p-3 text-left shadow-sm transition hover:border-gray-400 ${selectedEvent?.id === calendarEvent.id ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">{calendarEvent.title}</p>
            <p className="mt-1 flex items-center gap-1 text-xs text-gray-500"><Clock className="h-3 w-3" />{formatTime(calendarEvent.starts_at)} · {calendarEvent.meeting_type_label || readableStatus(calendarEvent.meeting_type_key || 'meeting')}</p>
          </div>
          <LocationIcon className="h-4 w-4 shrink-0 text-gray-400" />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge className={`border text-[11px] ${statusClass(calendarEvent.status)}`}>{readableStatus(calendarEvent.status)}</Badge>
          {calendarEvent.guests?.[0]?.display_name && <span className="truncate text-xs text-gray-500">{calendarEvent.guests[0].display_name}</span>}
        </div>
      </button>
    );
  };

  const renderCalendarGrid = () => {
    if (view === 'agenda') {
      return (
        <div className="space-y-3">
          {events.map(calendarEvent => (
            <div key={calendarEvent.id} className="grid gap-3 rounded-md border border-gray-200 bg-white p-4 sm:grid-cols-[120px_1fr]">
              <div className="text-sm font-semibold text-gray-900">{formatDate(calendarEvent.starts_at)}<div className="text-xs font-normal text-gray-500">{formatTime(calendarEvent.starts_at)}</div></div>
              <EventCard calendarEvent={calendarEvent} />
            </div>
          ))}
          {!events.length && !loading && <div className="rounded-md border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">No meetings in this range.</div>}
        </div>
      );
    }

    return (
      <div className={`grid gap-3 ${view === 'day' ? 'grid-cols-1' : view === 'month' ? 'grid-cols-1 md:grid-cols-7' : 'grid-cols-1 lg:grid-cols-7'}`}>
        {range.days.map(day => {
          const dayEvents = eventsByDay[dayKey(day)] || [];
          const inCurrentMonth = day.getMonth() === anchorDate.getMonth();
          return (
            <div key={dayKey(day)} className={`min-h-[180px] rounded-md border bg-white p-3 ${view === 'month' && !inCurrentMonth ? 'opacity-50' : ''}`}>
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(day)}</p>
                  <p className="text-lg font-semibold text-gray-900">{day.getDate()}</p>
                </div>
                {dayKey(day) === dayKey(new Date()) && <Badge className="border border-gray-200 bg-gray-50 text-gray-700">Today</Badge>}
              </div>
              <div className="space-y-2">
                {dayEvents.slice(0, view === 'month' ? 3 : 8).map(calendarEvent => <EventCard key={calendarEvent.id} calendarEvent={calendarEvent} />)}
                {dayEvents.length > 3 && view === 'month' && <p className="text-xs text-gray-500">+{dayEvents.length - 3} more</p>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-medium text-gray-500">
            <CalendarDays className="h-4 w-4" /> Ops
          </div>
          <h1 className="text-2xl font-display font-semibold text-[#242424]">Calendar</h1>
          <p className="mt-1 text-sm text-gray-500">{TENANT.branding.name} scheduling command center</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {message && <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700">{message}</Badge>}
          <Button variant="outline" onClick={() => setAnchorDate(new Date())}>Today</Button>
          <Button onClick={openCreate} className="bg-[#242424] text-white hover:bg-[#111111]"><Plus className="mr-2 h-4 w-4" />Create Event</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => moveRange(-1)}><ChevronLeft className="h-4 w-4" /></Button>
              <div className="min-w-[220px] text-center text-sm font-semibold text-gray-900">
                {formatDate(range.start)} - {formatDate(addDays(range.end, -1))}
              </div>
              <Button variant="outline" size="sm" onClick={() => moveRange(1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {(['day', 'week', 'month', 'agenda'] as CalendarView[]).map(option => (
                <Button key={option} variant={view === option ? 'default' : 'outline'} size="sm" onClick={() => setView(option)} className={view === option ? 'bg-[#242424] text-white' : ''}>{option[0].toUpperCase() + option.slice(1)}</Button>
              ))}
            </div>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <label className="text-xs font-medium text-gray-600"><ListFilter className="mr-1 inline h-3 w-3" />Status
              <select value={statusFilter} onChange={changeEvent => setStatusFilter(changeEvent.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                <option value="all">All statuses</option>
                {statusOptions.map(status => <option key={status} value={status}>{readableStatus(status)}</option>)}
              </select>
            </label>
            <label className="text-xs font-medium text-gray-600">Meeting type
              <select value={meetingTypeFilter} onChange={changeEvent => setMeetingTypeFilter(changeEvent.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                <option value="all">All meeting types</option>
                {meetingTypes.map(meetingType => <option key={meetingType.key} value={meetingType.key}>{meetingType.label}</option>)}
              </select>
            </label>
            <label className="text-xs font-medium text-gray-600">Host
              <select value={hostFilter} onChange={changeEvent => setHostFilter(changeEvent.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                <option value="all">All hosts</option>
                {hosts.map(host => <option key={host} value={host}>{host}</option>)}
              </select>
            </label>
            <label className="text-xs font-medium text-gray-600">Lead status
              <select value={leadStatusFilter} onChange={changeEvent => setLeadStatusFilter(changeEvent.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                <option value="all">All lead statuses</option>
                <option value="meeting_booked">Meeting Booked</option>
                <option value="call_completed">Meeting Completed</option>
                <option value="no_show">No Show</option>
                <option value="booking_cancelled">Booking Cancelled</option>
              </select>
            </label>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <Card>
          <CardContent className="p-4">
            {loading ? (
              <div className="flex h-72 items-center justify-center text-gray-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading calendar</div>
            ) : renderCalendarGrid()}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><Bell className="h-4 w-4" />Agenda</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {events.slice(0, 6).map(calendarEvent => (
                <button key={calendarEvent.id} type="button" onClick={() => setSelectedId(calendarEvent.id)} className="w-full rounded-md border border-gray-200 p-3 text-left hover:border-gray-400">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-gray-900">{calendarEvent.title}</span>
                    <span className="text-xs text-gray-500">{formatTime(calendarEvent.starts_at)}</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">{formatDate(calendarEvent.starts_at)} · {calendarEvent.guests?.[0]?.display_name || calendarEvent.lead_email || 'Internal'}</p>
                </button>
              ))}
              {!events.length && <p className="text-sm text-gray-500">No upcoming meetings.</p>}
            </CardContent>
          </Card>

          {selectedEvent && (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">{selectedEvent.title}</CardTitle>
                    <p className="mt-1 text-sm text-gray-500">{formatDate(selectedEvent.starts_at)} · {formatTime(selectedEvent.starts_at)} · {selectedEvent.timezone}</p>
                  </div>
                  <Badge className={`border ${statusClass(selectedEvent.status)}`}>{readableStatus(selectedEvent.status)}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Type</span><p className="font-medium text-gray-900">{selectedEvent.meeting_type_label || readableStatus(selectedEvent.meeting_type_key || 'Meeting')}</p></div>
                  <div><span className="text-gray-500">Host</span><p className="font-medium text-gray-900">{selectedEvent.host_name || 'Investor Relations'}</p></div>
                  <div><span className="text-gray-500">Campaign</span><p className="font-medium text-gray-900">{selectedEvent.event_campaign_name || selectedEvent.campaign_name || 'None'}</p></div>
                  <div><span className="text-gray-500">Source</span><p className="font-medium text-gray-900">{selectedEvent.source || selectedEvent.medium || 'Manual'}</p></div>
                </div>

                <label className="text-xs font-medium text-gray-600">Status
                  <select value={selectedEvent.status} onChange={changeEvent => updateStatus(selectedEvent, changeEvent.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                    {statusOptions.map(status => <option key={status} value={status}>{readableStatus(status)}</option>)}
                  </select>
                </label>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-600">Virtual meeting link</label>
                  <div className="flex gap-2">
                    <Input value={virtualLinkDraft} onChange={changeEvent => setVirtualLinkDraft(changeEvent.target.value)} placeholder="https://" />
                    <Button variant="outline" onClick={sendVirtualLinkUpdate}><Send className="h-4 w-4" /></Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-600"><Users className="mr-1 inline h-3 w-3" />Guests</p>
                    <span className="text-xs text-gray-500">{selectedEvent.guests?.length || 0}</span>
                  </div>
                  <div className="space-y-2">
                    {selectedEvent.guests?.map(guest => (
                      <div key={guest.id || guest.email} className="rounded-md border border-gray-200 p-2">
                        <p className="truncate text-sm font-medium text-gray-900">{guest.display_name || guest.email}</p>
                        <p className="truncate text-xs text-gray-500">{guest.email} · {readableStatus(guest.guest_type)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid gap-2">
                    <Input value={newGuest.display_name || ''} onChange={changeEvent => setNewGuest(previous => ({ ...previous, display_name: changeEvent.target.value }))} placeholder="Guest name" />
                    <Input value={newGuest.email || ''} onChange={changeEvent => setNewGuest(previous => ({ ...previous, email: changeEvent.target.value }))} placeholder="guest@example.com" />
                    <select value={newGuest.guest_type} onChange={changeEvent => setNewGuest(previous => ({ ...previous, guest_type: changeEvent.target.value }))} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                      {guestTypes.map(guestType => <option key={guestType} value={guestType}>{readableStatus(guestType)}</option>)}
                    </select>
                    <Button variant="outline" onClick={addGuest}><Plus className="mr-2 h-4 w-4" />Add Guest</Button>
                  </div>
                </div>

                <div className="space-y-2 border-t border-gray-100 pt-4">
                  <p className="text-xs font-medium text-gray-600"><ClipboardCheck className="mr-1 inline h-3 w-3" />Post-meeting</p>
                  <select value={followUp.meeting_outcome} onChange={changeEvent => setFollowUp(previous => ({ ...previous, meeting_outcome: changeEvent.target.value }))} className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                    {['Qualified', 'Needs Follow-Up', 'Not Qualified', 'Strategic Partner', 'Operator Prospect', 'Capital Partner', 'No Show', 'Reschedule Requested'].map(outcome => <option key={outcome} value={outcome}>{outcome}</option>)}
                  </select>
                  <Input value={followUp.interest_level} onChange={changeEvent => setFollowUp(previous => ({ ...previous, interest_level: changeEvent.target.value }))} placeholder="Interest level" />
                  <Input value={followUp.estimated_capital_range} onChange={changeEvent => setFollowUp(previous => ({ ...previous, estimated_capital_range: changeEvent.target.value }))} placeholder="Estimated capital range" />
                  <textarea value={followUp.recommended_next_action} onChange={changeEvent => setFollowUp(previous => ({ ...previous, recommended_next_action: changeEvent.target.value }))} placeholder="Recommended next action" className="min-h-[84px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
                  <Button variant="outline" onClick={saveFollowUp}><CheckCircle2 className="mr-2 h-4 w-4" />Add Follow-Up</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Create Calendar Event</DialogTitle></DialogHeader>
          <div className="max-h-[72vh] space-y-5 overflow-y-auto pr-1">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="text-sm font-medium text-gray-700 md:col-span-2">Event title<Input value={createForm.title} onChange={changeEvent => updateCreateForm('title', changeEvent.target.value)} /></label>
              <label className="text-sm font-medium text-gray-700">Meeting type
                <select value={createForm.meeting_type_key} onChange={changeEvent => handleMeetingTypeChange(changeEvent.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                  {meetingTypes.map(meetingType => <option key={meetingType.key} value={meetingType.key}>{meetingType.label}</option>)}
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700">Timezone<Input value={createForm.timezone} onChange={changeEvent => updateCreateForm('timezone', changeEvent.target.value)} /></label>
              <label className="text-sm font-medium text-gray-700">Start<Input type="datetime-local" value={createForm.starts_at} onChange={changeEvent => updateCreateForm('starts_at', changeEvent.target.value)} /></label>
              <label className="text-sm font-medium text-gray-700">End<Input type="datetime-local" value={createForm.ends_at} onChange={changeEvent => updateCreateForm('ends_at', changeEvent.target.value)} /></label>
              <label className="text-sm font-medium text-gray-700">Host name<Input value={createForm.host_name} onChange={changeEvent => updateCreateForm('host_name', changeEvent.target.value)} /></label>
              <label className="text-sm font-medium text-gray-700">Host email<Input value={createForm.host_email} onChange={changeEvent => updateCreateForm('host_email', changeEvent.target.value)} /></label>
              <label className="text-sm font-medium text-gray-700">Location type
                <select value={createForm.location_type} onChange={changeEvent => updateCreateForm('location_type', changeEvent.target.value)} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                  {locationOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              {createForm.location_type === 'virtual_meeting' && <label className="text-sm font-medium text-gray-700">Virtual link<Input value={createForm.virtual_meeting_url} onChange={changeEvent => updateCreateForm('virtual_meeting_url', changeEvent.target.value)} placeholder="https://" /></label>}
              {createForm.location_type === 'phone_call' && <label className="text-sm font-medium text-gray-700">Phone number<Input value={createForm.phone_number} onChange={changeEvent => updateCreateForm('phone_number', changeEvent.target.value)} /></label>}
              {createForm.location_type === 'in_person' && <><label className="text-sm font-medium text-gray-700">Venue<Input value={createForm.venue_name} onChange={changeEvent => updateCreateForm('venue_name', changeEvent.target.value)} /></label><label className="text-sm font-medium text-gray-700">Address<Input value={createForm.address} onChange={changeEvent => updateCreateForm('address', changeEvent.target.value)} /></label></>}
              <label className="text-sm font-medium text-gray-700 md:col-span-2">Objective<Input value={createForm.meeting_objective} onChange={changeEvent => updateCreateForm('meeting_objective', changeEvent.target.value)} /></label>
              <label className="text-sm font-medium text-gray-700 md:col-span-2">Description<textarea value={createForm.description} onChange={changeEvent => updateCreateForm('description', changeEvent.target.value)} className="mt-1 min-h-[90px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm" /></label>
              <label className="text-sm font-medium text-gray-700 md:col-span-2">Internal notes<textarea value={createForm.internal_notes} onChange={changeEvent => updateCreateForm('internal_notes', changeEvent.target.value)} className="mt-1 min-h-[90px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm" /></label>
            </div>

            <div className="rounded-md border border-gray-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Guests</h3>
                <Button variant="outline" size="sm" onClick={() => setGuestDrafts(previous => [...previous, { guest_type: DEFAULT_GUEST_TYPE, display_name: '', email: '', phone: '', is_required: true }])}><Plus className="mr-2 h-4 w-4" />Add Guest</Button>
              </div>
              <div className="space-y-3">
                {guestDrafts.map((guest, index) => (
                  <div key={`guest-${index}`} className="grid gap-2 md:grid-cols-[1fr_1fr_150px_90px]">
                    <Input value={guest.display_name || ''} onChange={changeEvent => setGuestDrafts(previous => previous.map((item, itemIndex) => itemIndex === index ? { ...item, display_name: changeEvent.target.value } : item))} placeholder="Name" />
                    <Input value={guest.email || ''} onChange={changeEvent => setGuestDrafts(previous => previous.map((item, itemIndex) => itemIndex === index ? { ...item, email: changeEvent.target.value } : item))} placeholder="Email" />
                    <select value={guest.guest_type} onChange={changeEvent => setGuestDrafts(previous => previous.map((item, itemIndex) => itemIndex === index ? { ...item, guest_type: changeEvent.target.value } : item))} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
                      {guestTypes.map(guestType => <option key={guestType} value={guestType}>{readableStatus(guestType)}</option>)}
                    </select>
                    <Button variant="outline" size="sm" onClick={() => setGuestDrafts(previous => previous.filter((_item, itemIndex) => itemIndex !== index))}>Remove</Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-4">
              <label className="text-sm font-medium text-gray-700">Source<Input value={createForm.source} onChange={changeEvent => updateCreateForm('source', changeEvent.target.value)} /></label>
              <label className="text-sm font-medium text-gray-700">Medium<Input value={createForm.medium} onChange={changeEvent => updateCreateForm('medium', changeEvent.target.value)} /></label>
              <label className="text-sm font-medium text-gray-700">Owner<Input value={createForm.owner} onChange={changeEvent => updateCreateForm('owner', changeEvent.target.value)} /></label>
              <label className="text-sm font-medium text-gray-700">Segment<Input value={createForm.segment} onChange={changeEvent => updateCreateForm('segment', changeEvent.target.value)} /></label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createEvent} disabled={saving} className="bg-[#242424] text-white hover:bg-[#111111]">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarDays className="mr-2 h-4 w-4" />}Create Event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCalendar;